import moment from 'moment-timezone';
import {
  doTitle,
  toggleLabelsButton,
  toggleMenuItemTitle,
  toggleShowAllButton,
  toMenuItemTitle,
  toggleButtonText,
  updateSeriesWithSortedLegend,
  updateUserOptionsEventSortOrder,
  updateUserOptionsEventSortType,
} from './Common';
import {
  sortingTypes,
  sortingOrder,
  sortingTypeBtnText,
  sortingOrderBtnText,
} from '../constants/common';

const LineChartCommonConfig = (
  chartSeries,
  options,
  fromDashboard = false,
  showCumulative,
  showSort,
  sortOrder,
  sortType,
  triggerLabels
) => {
  const adaptive = chartSeries.some((s) => s.isNegative);

  const lineChartConfig = {
    adaptive,
    medianLine: !!options.medianLine,
    averageLine: !!options.averageLine,
    trendLine: !!options.trendLine,
    cumulative: !!options.cumulative && showCumulative,
    groups: [
      'median',
      'average',
      'trend',
      'adaptive',
      showCumulative && 'cumulative',
      !fromDashboard && 'zoomSync',
      options.group,
    ],
    time: {
      getTimezoneOffset(timestamp) {
        return -moment.tz(timestamp, options.timezone).utcOffset();
      },
    },
    eventSort: {
      sortOrder,
      sortType,
    },
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          menuItems: [
            'adaptive',
            'trendLine',
            'averageLine',
            'medianLine',
            showCumulative && 'cumulative',
            'separator',
            'printChart',
            'separator',
            'downloadPNG',
            'downloadJPEG',
            'downloadPDF',
            'downloadSVG',
          ],
        },
        Fullscreen: {
          verticalAlign: 'bottom',
          align: 'left',
          text: 'full screen',
          onclick() {
            this.fullscreen.toggle();
          },
        },
        showAll: {
          verticalAlign: 'bottom',
          align: 'left',
          text: doTitle('show all', false),
          onclick() {
            if (this.series.reduce((r, s) => s.visible && r, true)) {
              this.series.forEach((s) => {
                s.setVisible(false, false);
              });
            } else {
              this.series.forEach((s) => {
                s.setVisible(true, false);
              });
            }
            toggleShowAllButton(this);
            this.redraw();
          },
        },
        ...(showSort && {
          toggleSortOrder: {
            verticalAlign: 'bottom',
            align: 'left',
            text:
              sortOrder === sortingOrder.ASCENDING
                ? sortingOrderBtnText.ASCENDING
                : sortingOrderBtnText.DESCENDING,
            onclick() {
              this.userOptions.eventSort = updateUserOptionsEventSortOrder(this);

              updateSeriesWithSortedLegend(this.series, chartSeries, this.userOptions.eventSort);

              toggleButtonText(
                this.exportSVGElements,
                this.userOptions.eventSort.sortOrder,
                sortingOrder.ASCENDING,
                sortingOrderBtnText.ASCENDING,
                sortingOrderBtnText.DESCENDING
              );

              this.redraw();
            },
          },
        }),
        ...(showSort && {
          toggleSortType: {
            verticalAlign: 'bottom',
            align: 'left',
            text: sortType === sortingTypes.NAME ? sortingTypeBtnText.NAME : sortingTypeBtnText.SUM,
            onclick() {
              this.userOptions.eventSort = updateUserOptionsEventSortType(this);

              updateSeriesWithSortedLegend(this.series, chartSeries, this.userOptions.eventSort);

              toggleButtonText(
                this.exportSVGElements,
                this.userOptions.eventSort.sortType,
                sortingTypes.NAME,
                sortingTypeBtnText.NAME,
                sortingTypeBtnText.SUM
              );

              this.redraw();
            },
          },
        }),
        ...(fromDashboard && {
          labels: {
            verticalAlign: 'bottom',
            align: 'left',
            text: doTitle('labels', true),
            onclick() {
              if (!this.labels) {
                this.zoomOut();
              }
              toggleLabelsButton(this);
              triggerLabels();
            },
          },
        }),
      },
      menuItemDefinitions: {
        adaptive: {
          verticalAlign: 'bottom',
          align: 'left',
          text: toMenuItemTitle('adaptive', true),
          onclick(e) {
            const isZero = this.series
              .filter((s) => s.visible)
              .flatMap(({ data }) => data)
              .some(({ y }) => y === 0);
            // если в данных присутствует 0, то adaptive не имеет смысла
            if (!isZero) {
              toggleMenuItemTitle(e, this, 'adaptive', 'adaptive');
              this.userOptions.adaptive = !this.userOptions.adaptive;
              this.yAxis[0].update({ min: this.yAxis[0].min === 0 ? undefined : 0 });
            }
          },
        },
        ...(showCumulative && {
          cumulative: {
            verticalAlign: 'bottom',
            align: 'left',
            text: toMenuItemTitle('cumulative', true),
            onclick(e) {
              toggleMenuItemTitle(e, this, 'cumulative', 'cumulative');
              this.userOptions.cumulative = !this.userOptions.cumulative;
              this.redraw();
            },
          },
        }),
        trendLine: {
          verticalAlign: 'bottom',
          align: 'left',
          text: toMenuItemTitle('trend', true),
          onclick(e) {
            toggleMenuItemTitle(e, this, 'trendLine', 'trend');
            this.userOptions.trendLine = !this.userOptions.trendLine;
            this.redraw();
          },
        },
        averageLine: {
          verticalAlign: 'bottom',
          align: 'left',
          text: toMenuItemTitle('average', true),
          onclick(e) {
            toggleMenuItemTitle(e, this, 'averageLine', 'average');
            this.userOptions.averageLine = !this.userOptions.averageLine;
            this.redraw();
          },
        },
        medianLine: {
          verticalAlign: 'bottom',
          align: 'left',
          text: toMenuItemTitle('median', true),
          onclick(e) {
            toggleMenuItemTitle(e, this, 'medianLine', 'median');
            this.userOptions.medianLine = !this.userOptions.medianLine;
            this.redraw();
          },
        },
      },
    },
    yAxis: {
      min: !!options.adaptive || adaptive ? undefined : 0,
      title: {
        text: null,
      },
    },
    title: {
      text: '',
    },
  };

  if (fromDashboard) {
    delete lineChartConfig.exporting.buttons.Fullscreen;
  }

  if (fromDashboard && triggerLabels === undefined) {
    delete lineChartConfig.exporting.buttons.labels;
  }

  return lineChartConfig;
};

export default LineChartCommonConfig;
